import type { Recipe } from '@/types'

import { useMemo, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { format, addDays } from 'date-fns'
import { ArrowLeft, ChevronLeft, ChevronRight, Plus, ShoppingCart } from 'lucide-react'

import {
  Button, Card, CardContent, CardFooter, CardHeader, CardTitle, Spinner,
} from '@/components/ui'
import { MixLayout } from '@/components/layout'
import { useWebSocketContext } from '@/contexts'
import { mixService } from '@/services'
import { toast } from 'sonner'

interface ConfirmDialogState {
  open: boolean
  recipe: Recipe | null
  slotName: string
  date: string
}

export interface ScheduleViewProps {
  mixId: string
}

export default function ScheduleView({ mixId }: ScheduleViewProps) {
  const navigate = useNavigate()
  const { mixes, isMix } = useWebSocketContext()

  const [confirm, setConfirm] = useState<ConfirmDialogState>({
    open: false,
    recipe: null,
    slotName: '',
    date: '',
  })

  const mix = useMemo(() => {
    return isMix(mixId) ? mixes.find((m) => m.id === mixId) ?? null : null
  }, [mixId, isMix, mixes])

  const days = useMemo(() => {
    if (!mix?.startedAt || !mix?.numberOfDays) return []
    const start = new Date(mix.startedAt + 'T00:00:00')
    return Array.from({ length: mix.numberOfDays }, (_, i) => addDays(start, i))
  }, [mix?.startedAt, mix?.numberOfDays])

  const slots = useMemo(() => {
    return mix?.mealSlotOrder ?? []
  }, [mix?.mealSlotOrder])

  const mealsPlanned = useMemo(() => {
    return mix?.recipes.filter((r) => !!r.scheduledAt).length ?? 0
  }, [mix?.recipes])

  const totalSlots = useMemo(() => {
    return (mix?.numberOfDays ?? 0) * slots.length
  }, [mix?.numberOfDays, slots.length])

  const dateRange = useMemo(() => {
    if (!mix?.startedAt || !mix?.numberOfDays) return ''
    const start = new Date(mix.startedAt + 'T00:00:00')
    const end = addDays(start, mix.numberOfDays - 1)
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
    if (sameMonth) {
      return `${format(start, 'MMM d')}–${format(end, 'd')}`
    }
    return `${format(start, 'MMM d')}–${format(end, 'MMM d')}`
  }, [mix?.startedAt, mix?.numberOfDays])

  const getRecipeForSlot = useCallback(
    (date: Date, slotId: string): Recipe | undefined => {
      const yyyy = date.getFullYear()
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const dateStr = `${yyyy}-${mm}-${dd}`
      return mix?.recipes.find((r) => r.scheduledAt === dateStr && r.mealSlotId === slotId)
    },
    [mix?.recipes]
  )

  // Refs and scroll state for each day's horizontal slot carousel
  const scrollContainerRefs = useRef<Record<number, HTMLDivElement>>({})
  const [scrollStates, setScrollStates] = useState<Record<number, { canLeft: boolean; canRight: boolean }>>({})

  const updateScrollState = useCallback((dayIndex: number) => {
    const el = scrollContainerRefs.current[dayIndex]
    if (!el) return
    const canLeft = el.scrollLeft > 1
    const canRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    setScrollStates((prev) => ({ ...prev, [dayIndex]: { canLeft, canRight } }))
  }, [])

  const scrollSlot = useCallback((dayIndex: number, direction: 'left' | 'right') => {
    const el = scrollContainerRefs.current[dayIndex]
    if (!el || slots.length === 0) return
    const firstChild = el.firstElementChild as HTMLElement
    const scrollAmount = firstChild ? firstChild.offsetWidth + 12 : el.clientWidth * 0.85
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' })
  }, [slots.length])

  const handleRemoveRecipe = async () => {
    if (!mix || !confirm.recipe) return

    const updatedRecipes = mix.recipes.filter(
      (r) => !(r.id === confirm.recipe!.id && r.scheduledAt === confirm.recipe!.scheduledAt && r.mealSlotId === confirm.recipe!.mealSlotId)
    )

    const recipeIngredientIds = new Set(confirm.recipe!.ingredientIds)
    const updatedIngredients = mix.ingredients.filter(
      (i) => !(recipeIngredientIds.has(i.id) && i.scheduledAt === confirm.recipe!.scheduledAt && i.mealSlotId === confirm.recipe!.mealSlotId)
    )

    try {
      await mixService.updateMix(mix.id, {
        name: mix.name,
        recipes: updatedRecipes,
        ingredients: updatedIngredients,
      })
      toast.success(`${confirm.recipe.name} removed from schedule`)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove recipe'
      toast.error(message)
    } finally {
      setConfirm({ open: false, recipe: null, slotName: '', date: '' })
    }
  }

  if (!mix) {
    return (
      <MixLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Spinner className="size-12" />
        </div>
      </MixLayout>
    )
  }

  return (
    <MixLayout>
      <div className="flex flex-col min-h-0">
        {/* Sticky header */}
        <div className="sticky top-0 bg-background z-30 px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/home')}
              className="h-10 w-10 flex items-center justify-center rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-foreground truncate">{mix.name}</h1>
              {dateRange && (
                <p className="text-xs text-muted-foreground">{dateRange}</p>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable day cards */}
        <div className="flex-1 overflow-y-auto px-4 py-4 pb-24">
          <div className="space-y-4 mx-auto">
            {days.map((day, dayIndex) => {
              const yyyy = day.getFullYear()
              const mm = String(day.getMonth() + 1).padStart(2, '0')
              const dd = String(day.getDate()).padStart(2, '0')
              const dateStr = `${yyyy}-${mm}-${dd}`
              const dayRecipes = mix.recipes.filter((r) => r.scheduledAt === dateStr)

              return (
                <Card key={dateStr} className="border-border">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">
                          {format(day, 'EEEE, MMM d')}
                        </CardTitle>
                        <p className="text-xs text-muted-foreground">
                          Day {dayIndex + 1} · {dayRecipes.length} meal{dayRecipes.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 sm:hidden">
                        <button
                          type="button"
                          aria-label="Previous slot"
                          onClick={() => scrollSlot(dayIndex, 'left')}
                          className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors ${scrollStates[dayIndex]?.canLeft ? 'text-foreground hover:bg-muted' : 'text-muted-foreground opacity-30 cursor-default'}`}
                          disabled={!scrollStates[dayIndex]?.canLeft}
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          aria-label="Next slot"
                          onClick={() => scrollSlot(dayIndex, 'right')}
                          className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors ${scrollStates[dayIndex]?.canRight ? 'text-foreground hover:bg-muted' : 'text-muted-foreground opacity-30 cursor-default'}`}
                          disabled={!scrollStates[dayIndex]?.canRight}
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div
                      ref={(el) => {
                        if (el && !scrollContainerRefs.current[dayIndex]) {
                          scrollContainerRefs.current[dayIndex] = el
                          requestAnimationFrame(() => updateScrollState(dayIndex))
                        }
                      }}
                      onScroll={() => updateScrollState(dayIndex)}
                      className="flex sm:grid sm:grid-cols-3 gap-3 overflow-x-auto snap-x snap-mandatory sm:overflow-visible pb-2 sm:pb-0"
                    >
                      {slots.map((slot) => {
                        const recipe = getRecipeForSlot(day, slot.id)

                        if (recipe) {
                          return (
                            <button
                              key={slot.id}
                              type="button"
                              onClick={() =>
                                setConfirm({
                                  open: true,
                                  recipe,
                                  slotName: slot.name,
                                  date: format(day, 'EEEE, MMM d'),
                                })
                              }
                              className="flex-shrink-0 w-[85%] sm:w-auto snap-start relative rounded-lg border border-border bg-card overflow-hidden text-left transition-colors hover:bg-accent hover:border-primary/50 cursor-pointer"
                            >
                              {recipe.image ? (
                                <div className="aspect-video overflow-hidden">
                                  <img
                                    src={recipe.image}
                                    alt={recipe.name}
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none'
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="aspect-video flex items-center justify-center bg-muted">
                                  <span className="text-muted-foreground text-xs">No image</span>
                                </div>
                              )}
                              <div className="px-2 py-2">
                                <p className="text-sm font-medium truncate text-foreground">
                                  {recipe.name}
                                </p>
                                <p className="text-xs text-muted-foreground">{slot.name}</p>
                              </div>
                            </button>
                          )
                        }

                        return (
                          <button
                            key={slot.id}
                            type="button"
                            onClick={() =>
                              navigate(`/schedules/${mixId}/add-recipe?date=${dateStr}&slot=${slot.id}`)
                            }
                            className="flex-shrink-0 w-[85%] sm:w-auto snap-start relative rounded-lg border border-dashed border-border bg-card overflow-hidden text-left transition-colors hover:bg-accent hover:border-primary/50 cursor-pointer"
                          >
                            <div className="aspect-video flex flex-col items-center justify-center gap-1">
                              <Plus className="h-5 w-5 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">{slot.name}</span>
                            </div>
                            <div className="px-2 py-2">
                              <p className="text-sm font-medium truncate text-muted-foreground">
                                {slot.name}
                              </p>
                              <p className="text-xs text-muted-foreground">Add recipe</p>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>

        {/* Persistent bottom bar */}
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card p-4 shadow-lg text-foreground">
          <div className="w-full max-w-[768px] mx-auto flex items-center justify-between gap-4 px-4">
            <span className="text-sm font-medium">
              {mealsPlanned} of {totalSlots} meals planned
            </span>
            <Button onClick={() => navigate(`/mixes/${mixId}/grocerylist`)}>
              <ShoppingCart className="size-4" />
              Grocery List →
            </Button>
          </div>
        </div>
      </div>

      {/* Remove recipe confirmation dialog */}
      {confirm.open && confirm.recipe && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setConfirm({ open: false, recipe: null, slotName: '', date: '' })}
        >
          <div className="mx-4 w-full max-w-sm">
            <Card onClick={(e) => e.stopPropagation()}>
              <CardHeader className="text-center">
                <CardTitle>Remove Recipe</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground text-center">
                  Are you sure you want to remove{' '}
                  <span className="font-medium text-foreground">{confirm.recipe.name}</span>
                  {' '}from{' '}
                  <span className="font-medium text-foreground">{confirm.slotName}</span>
                  {' '}on{' '}
                  <span className="font-medium text-foreground">{confirm.date}</span>?
                </p>
              </CardContent>
              <CardFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirm({ open: false, recipe: null, slotName: '', date: '' })}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRemoveRecipe}
                  variant="destructive"
                  className="flex-1"
                >
                  Remove
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      )}
    </MixLayout>
  )
}
